import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom/'
import { isEmpty, isNil } from 'lodash'
import format from 'date-fns/format'
import { differenceInDays } from 'date-fns'
import { IconInfoCircle, IconPlus } from '@tabler/icons-react'

import { AiSparkles } from '@/components/ai/AiSparkles'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import Select from '@/components/forms/Select'
import { TextInput } from '@/components/forms/TextInput'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { NestedSelect } from '@/components/forms/NestedSelect'
import { Accordion } from '@/components/accordions/Accordion'
import {
  clearAllFilters,
  clearAllFiltersAndFetchCalls,
  fetchAgentsByOrg,
  fetchCalls,
  fetchAiAgentDisconnectionReasons,
  fetchAiAgentOutcomes,
  fetchDispositionsByOrg,
  fetchFilterableBitFields,
  fetchOutboundCampaignsByOrg,
  fetchPlaybookData,
  fetchPlaybooksByOrg,
  fetchScorecardsByOrg,
  fetchTagsByOrg,
  fetchVoipCampaignsByOrg,
} from '@/reducers/callSearch/callSearch.actions'
import {
  initialState,
  setData,
  setDateRangeFilters,
  setFilters,
} from '@/reducers/callSearch/callSearch.redux'
import { formatNestedOptions } from '@/components/helpers/nestedSelectHelpers'
import {
  filterValueHasChanged,
  getFilterDisplayValues,
  getHierarchyOrganizationOptions,
  getOrganizationOptions,
  getScorecardOptions,
  hasChannelAccess,
} from '@/utils/helpers'
import {
  attachmentOptions,
  directionOptions,
  minCallDurationOptions,
  sentimentOptions,
  CHANNEL_TYPE,
  CHANNELS_WITH_CUSTOMER_ID,
  agentTypeOptions,
} from '@/utils/constants'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { useTogoEnabled } from '@/utils/hooks/useTogoEnabled'
import { OrganizationFilter } from '@/components/filters/OrganizationFilter'
import {
  clearEventSection,
  toggleEventCategory,
  toggleEventEntry,
  toggleEventIncludes,
} from '@/components/filters/eventFilter.helpers'
import { WinsFilter } from '@/components/filters/WinsFilter'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { CALL_EXPLORER_FILTER_TYPE } from '@/reducers/savedFilters/savedFilters.constants'
import { createSavedFilter } from '@/reducers/savedFilters/savedFilters.actions'
import { getDateRangeKey, getDateRangeLabel } from '@/components/helpers'
import { Button } from '@/components/buttons/Button'
import { PartialScoresFilter } from '@/components/filters/PartialScoresFilter'
import { FormField } from '@/components/forms/FormField'
import { VoipCustomerSearch } from '@/views/Calls/components/VoipCustomerSearch'

import { KeywordSearch } from './components/KeywordSearch'
import { CallSearchPills } from './components/CallSearchPills'
import { SaveCallFilter } from './components/SaveCallFilter'
import { AI_AGENT_FILTERS, KEYWORD_FILTERS, NOTETAKER_FILTERS } from './CallSearch.constants'
import {
  getSelectedPlaybookCids,
  buildBitFieldOptions,
  isBitFieldOptionDisabled,
  renderBitFieldOption,
  isBooleanBitField,
  BOOLEAN_FIELD_OPTIONS,
} from './bitFieldFilter.helpers'

// Portal the Notetaker option menus out of the accordion, whose `overflow:
// hidden` would otherwise clip them; disabled in tests so the menu stays inline
// for assertions (mirrors components/forms/Select.js).
const MENU_PORTAL_TARGET =
  process.env.NODE_ENV !== 'test' && typeof document !== 'undefined' ? document.body : undefined

const CallSearchFiltersComponent = ({
  params,
  childOrgSelected,
  allow120Days = false,
  unlimitedDateRange = false,
  openFiltersRequested,
  onOpenFiltersHandled,
  hiddenFilters = [],
  onApplyFilters,
  onClearFilters,
}) => {
  const dispatch = useDispatch()
  const location = useLocation()
  // Sections a host page cannot act on. Hidden rather than disabled: a filter
  // the page will not apply is worse than absent, because the numbers come back
  // looking like it was honoured.
  const isHidden = (name) => hiddenFilters.includes(name)
  // Fires at the points Call Explorer refetches, and only those: applying the
  // modal, changing or removing a pill, saving a date range, switching
  // organization, clearing. The modal writes each selection to the slice as it
  // is made, so a host that reloaded on every filter change would issue a
  // request per intermediate selection.
  const applyFilters = () => onApplyFilters?.()
  const [modalOpen, setModalOpen] = useState(false)
  const [dateInvalid, setDateInvalid] = useState(false)
  const [initialFiltersState, setInitialFiltersState] = useState(null)
  const [agentSearchQuery, setAgentSearchQuery] = useState('')
  const [notetakerExpanded, setNotetakerExpanded] = useState(false)
  const { filters, loading, data } = useSelector((state) => state.callSearch)
  const { organizations } = useSelector((state) => state)
  const voipCampaignsOptions = data.voipCampaigns
  const { voipCampaignsTimedOut } = data
  const {
    organizationid: currentUserOrg,
    hierarchy_manager,
    edit_qa: editQA,
    edit_qa_copilot: editQACopilot,
  } = useSelector((state) => state.currentUser)
  const { qa_enabled: qaEnabled, qa_copilot_enabled: qaCopilotAccess } = useSelector(
    (state) => state.currentOrganization
  )
  const { audio_ingestion_only: isAudioIngestionOnly } = useSelector(
    (state) => state.currentOrganization
  )
  const { outbound_campaigns_enabled: currentOrgOutboundCampaignsEnabled } = useSelector(
    (state) => state.currentOrganization
  )
  const { startDate, endDate, organizationId } = filters
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const { active_channels: activeChannels = [] } = useSelector((state) => state.currentOrganization)

  const organizationOptions = getOrganizationOptions(organizations)
  const hierarchyOrganizationOptions = getHierarchyOrganizationOptions(flattenedUserOrgHierarchy)
  const isBaltoAdmin = currentUserOrg === 1

  const orgActiveChannels = useMemo(() => {
    if (isBaltoAdmin && filters.organizationId) {
      const selectedOrg = organizations?.find((org) => org.id === filters.organizationId)
      return selectedOrg?.active_channels || []
    }
    return activeChannels
  }, [isBaltoAdmin, filters.organizationId, organizations, activeChannels])

  const outboundCampaignsEnabled = useMemo(() => {
    if (isBaltoAdmin && filters.organizationId) {
      const selectedOrg = organizations?.find((org) => org.id === filters.organizationId)
      return Boolean(selectedOrg?.outbound_campaigns_enabled)
    }
    return Boolean(currentOrgOutboundCampaignsEnabled)
  }, [isBaltoAdmin, filters.organizationId, organizations, currentOrgOutboundCampaignsEnabled])

  const togoEnabled = useTogoEnabled(organizationId)
  const showManagerHierarchyOrgDropdown = !isBaltoAdmin && hierarchy_manager
  const hasOrganizationDropdown = isBaltoAdmin || showManagerHierarchyOrgDropdown
  const validOrgId = organizationId !== '' && organizationId !== 0
  const callDurationInvalid =
    Boolean(filters.callDuration) &&
    Boolean(filters.maxCallDuration) &&
    Number(filters.callDuration) >= Number(filters.maxCallDuration)
  const canEditQA = isBaltoAdmin || (qaEnabled && editQA)
  const canEditCopilotQA = isBaltoAdmin || (qaCopilotAccess && editQACopilot)
  const showScorecards = canEditQA || canEditCopilotQA

  const scorecardOptions = useMemo(() => {
    return organizationId ? getScorecardOptions(data.scorecardConfigs) : []
  }, [data.scorecardConfigs, organizationId])

  const isCallExplorerPage = location.pathname.startsWith('/explorer')

  // The Notetaker (bit-field) filters are grouped under one collapsed section,
  // so expand it on open when any (pick_list or boolean) are already applied.
  const hasActiveNotetakerFilters =
    Object.values(filters.bitFields).some((selected) => !isEmpty(selected)) ||
    Object.values(filters.booleanFields ?? {}).some((value) => !isNil(value))
  useEffect(() => {
    if (modalOpen) {
      setNotetakerExpanded(hasActiveNotetakerFilters)
    }
  }, [modalOpen])

  useEffect(() => {
    // Don't set default organization if Balto is pre-loading data from another org, because it will get set elsewhere
    if (!(isBaltoAdmin && params?.organizationId)) {
      dispatch(setFilters({ organizationId: currentUserOrg }))
    }
  }, [])

  useEffect(() => {
    if (validOrgId) {
      dispatch(fetchAgentsByOrg(organizationId))
      dispatch(fetchTagsByOrg(organizationId))
      dispatch(fetchPlaybooksByOrg(organizationId))
      dispatch(fetchScorecardsByOrg(organizationId))
      dispatch(fetchVoipCampaignsByOrg(organizationId))
      dispatch(fetchDispositionsByOrg(organizationId))
      dispatch(fetchFilterableBitFields(organizationId))
    }
  }, [organizationId])

  useEffect(() => {
    if (validOrgId && outboundCampaignsEnabled) {
      dispatch(fetchOutboundCampaignsByOrg(organizationId))
    }
  }, [organizationId, outboundCampaignsEnabled])

  useEffect(() => {
    if (validOrgId && togoEnabled) {
      dispatch(fetchAiAgentOutcomes())
      dispatch(fetchAiAgentDisconnectionReasons())
    }
  }, [organizationId, togoEnabled])

  useEffect(() => {
    const selectedPlaybooks = data.playbookData.filter((playbook) => {
      const loadedCids = filters.playbooks.map((loadedPlaybook) => {
        return loadedPlaybook.value
      })
      return loadedCids.includes(playbook.cid)
    })
    const selectedFilters = { checklist: [], notifications: [], deck: [], postcall: [] }

    selectedPlaybooks.forEach(({ playbook_name, cid }) => {
      selectedFilters.checklist.push(
        ...filters.checklist.selected.filter((item) => item.includes(playbook_name))
      )
      selectedFilters.notifications.push(
        ...filters.notifications.selected.filter((item) => item.includes(playbook_name))
      )
      selectedFilters.deck.push(...filters.deck.selected.filter((item) => item.includes(cid)))
      selectedFilters.postcall.push(
        ...filters.postcall.selected.filter((item) => item.includes(cid))
      )
    })

    Object.entries(selectedFilters).forEach(([key, selected]) => {
      dispatch(setFilters({ [key]: { selected, includes: true } }))
    })
  }, [data.playbookData])

  useEffect(() => {
    if (filters.playbooks.length > 0) {
      dispatch(fetchPlaybookData(filters))
    } else {
      dispatch(setData({ playbookData: [] }))
      const initialPlaybookEventsFilters = { includes: true, selected: [] }
      dispatch(
        setFilters({
          checklist: initialPlaybookEventsFilters,
          notifications: initialPlaybookEventsFilters,
          deck: initialPlaybookEventsFilters,
          postcall: initialPlaybookEventsFilters,
          openCategories: initialPlaybookEventsFilters,
          openSubcategories: initialPlaybookEventsFilters,
        })
      )
    }
  }, [filters.playbooks])

  useEffect(() => {
    if (isNil(filters.scorecards)) {
      dispatch(setFilters({ scorecards: [] }))
    }
    if (isEmpty(filters.scorecards)) {
      dispatch(setFilters({ scoredStatus: '', scorecardThreshold: '' }))
    }
  }, [filters.scorecards])

  useEffect(() => {
    const initialOpenSubcategories = []
    const initialOpenDeckCategories = []
    const initialOpenPostcallCategories = []
    const initialOpenChecklistCategories = []
    const initialOpenNotificationCategories = []

    data.playbookData?.forEach((playbook) => {
      const { cid } = playbook
      playbook.deck.forEach((category) => {
        const categoryContext = `${cid} - deck - ${category.category}`
        category.items.forEach((item) => {
          if (item.has_sub_items) {
            initialOpenSubcategories.push(`${categoryContext} - ${item.item}`)
          }
        })
        initialOpenDeckCategories.push(categoryContext)
      })

      if (playbook.postcall) {
        playbook.postcall.forEach((category) => {
          const categoryContext = `${cid} - postcall - ${category.category}`
          initialOpenPostcallCategories.push(categoryContext)
          category.items?.forEach((item) => {
            initialOpenSubcategories.push(`${categoryContext} - ${item}`)
          })
        })
      }

      if (playbook.notifications) {
        const categoryContext = `${cid} - notifications - ${playbook.playbook_name.replaceAll(' - ', '-')}`
        initialOpenNotificationCategories.push(categoryContext)

        playbook.notifications.forEach((item) => {
          const subCatContext = `${categoryContext} - ${item}`
          initialOpenSubcategories.push(subCatContext)
        })
      }

      if (playbook.checklist) {
        const categoryContext = `${cid} - checklist - ${playbook.playbook_name.replaceAll(' - ', '-')}`
        initialOpenChecklistCategories.push(categoryContext)

        playbook.checklist.forEach((item) => {
          const subCatContext = `${categoryContext} - ${item}`
          initialOpenSubcategories.push(subCatContext)
        })
      }
    })

    const initialOpenCategories = [
      ...initialOpenDeckCategories,
      ...initialOpenPostcallCategories,
      ...initialOpenNotificationCategories,
      ...initialOpenChecklistCategories,
    ]

    dispatch(
      setFilters({
        openCategories: initialOpenCategories,
        openSubcategories: initialOpenSubcategories,
      })
    )
  }, [data.playbookData])

  const filterIsDisabled = () => {
    // Disable button if organization is not selected for admin, or hierarchy manager
    if (
      (isBaltoAdmin && !filters.organizationId) ||
      (showManagerHierarchyOrgDropdown && !filters.organizationId)
    ) {
      return 'Organization is required to apply filters'
    }

    // Disable button if date is over 62 days
    if (dateInvalid) {
      return `Date range must be ${!allow120Days ? '60' : '120'} days or less`
    }

    // Max call duration is less than min call duration
    if (callDurationInvalid) {
      return 'Min call duration must be less than max call duration'
    }

    return false
  }

  const handleDismissModal = () => {
    if (initialFiltersState) {
      dispatch(setFilters(initialFiltersState))
    }

    setModalOpen(false)
  }

  const handleOpenModal = () => {
    setModalOpen(true)
    setInitialFiltersState(filters)
  }

  useEffect(() => {
    if (openFiltersRequested) {
      handleOpenModal()
      onOpenFiltersHandled()
    }
  }, [openFiltersRequested])

  const handleCloseModal = () => {
    setModalOpen(false)
    setInitialFiltersState(null)
  }

  const handleClearRange = () => {
    const defaultStartDate = format(new Date(), "yyyy-MM-dd'T00:00:00'xxx")
    const defaultEndDate = format(new Date(), "yyyy-MM-dd'T23:59:59'xxx")

    dispatch(setDateRangeFilters({ startDate: defaultStartDate, endDate: defaultEndDate }))
    setDateInvalid(false)

    if (filters.playbooks.length >= 1) {
      dispatch(
        fetchPlaybookData({
          ...filters,
          startDate: defaultStartDate,
          endDate: defaultEndDate,
        })
      )
    }

    if (isCallExplorerPage) {
      dispatch(
        fetchCalls({
          filters: {
            ...filters,
            startDate: defaultStartDate,
            endDate: defaultEndDate,
            // The URL carries the picked preset, so a stale one would rename Today after a reload.
            dateRange: undefined,
            voipCampaigns: [],
          },
          playbookData: data.playbookData,
        })
      )
    }
    applyFilters()
  }

  const handleUpdateRange = ({ selection }, shouldApplyFilters = false) => {
    const currentStartDate = format(selection.startDate, "yyyy-MM-dd'T00:00:00'xxx")
    const currentEndDate = format(selection.endDate, "yyyy-MM-dd'T23:59:59'xxx")
    const dateRange = getDateRangeKey(selection.text)

    const datesDiff = differenceInDays(selection.endDate, selection.startDate)
    const maxDateRange = !allow120Days ? 62 : 122

    if (!unlimitedDateRange && datesDiff > maxDateRange) {
      setDateInvalid(true)
    } else {
      setDateInvalid(false)
      dispatch(
        setDateRangeFilters({ startDate: currentStartDate, endDate: currentEndDate, dateRange })
      )

      if (filters.playbooks.length >= 1) {
        dispatch(
          fetchPlaybookData({ ...filters, startDate: currentStartDate, endDate: currentEndDate })
        )
      }

      // Why these three conditions?
      // 1. isCallExplorerPage - because this component is shared with Insights, and should never apply for insights
      // 2. shouldApplyFilters - because that condition is passed into CallSearchPills so the fetch can apply automatically when a preset is selected
      // 3. selection.text - because if selection text is not present, then a preset has not been selected, and we don't want to auto fetch a single day
      if (shouldApplyFilters && selection.text) {
        if (isCallExplorerPage) {
          dispatch(
            fetchCalls({
              filters: {
                ...filters,
                startDate: currentStartDate,
                endDate: currentEndDate,
                dateRange,
              },
              playbookData: data.playbookData,
            })
          )
        }
        applyFilters()
      }
    }
  }

  useEffect(() => {
    if (filters.organizationId) {
      dispatch(fetchVoipCampaignsByOrg(filters.organizationId))
    }
  }, [filters.startDate, filters.endDate])

  const handleUpdateFilters = (section, value) => {
    dispatch(setFilters({ [section]: value }))
  }

  // Each pick_list field is its own filter; track selections keyed by field_id.
  const handleUpdateBitField = (fieldId, value) => {
    dispatch(setFilters({ bitFields: { ...(filters.bitFields ?? {}), [fieldId]: value } }))
  }

  // Boolean fields store a single true/false keyed by field_id; clearing removes
  // the entry so it isn't sent as a filter.
  const handleUpdateBooleanField = (fieldId, value) => {
    const nextBooleanFields = { ...(filters.booleanFields ?? {}) }
    if (isNil(value)) {
      delete nextBooleanFields[fieldId]
    } else {
      nextBooleanFields[fieldId] = value
    }
    dispatch(setFilters({ booleanFields: nextBooleanFields }))
  }

  const handleSubmitFilters = (event) => {
    if (event && typeof event.preventDefault === 'function') {
      event.preventDefault()
    }

    handleCloseModal()

    if (isCallExplorerPage) {
      dispatch(fetchCalls({ filters, playbookData: data.playbookData }))
    }
    applyFilters()
  }

  const handleClearFilters = () => {
    // A host whose defaults differ from the slice's clears for itself - the
    // slice restores Call Explorer's defaults, which are not universal.
    if (onClearFilters) {
      onClearFilters()
    } else if (isCallExplorerPage) {
      dispatch(clearAllFiltersAndFetchCalls())
    } else {
      dispatch(clearAllFilters())
    }
    applyFilters()
  }

  const handleOrgSelect = (option) => {
    // Reset dependent filters on org change
    dispatch(
      setFilters({
        agents: [],
        tags: [],
        playbooks: [],
        dispositions: { selected: [], includes: true },
        voipCampaigns: [],
        outboundCampaigns: [],
        aiAgentOutcomes: [],
        aiAgentDisconnectionReasons: [],
        // Keyed by field_id, so a stale selection could otherwise leak into the
        // next org's search and filter against a field that isn't theirs.
        bitFields: {},
        booleanFields: {},
      })
    )

    handleUpdateFilters('organizationId', option.value)
  }

  const handleToggleEntry = (option) => {
    dispatch(setFilters(toggleEventEntry(filters, option)))
  }

  const clearAllEntries = (section) => {
    dispatch(setFilters(clearEventSection(filters, section)))
  }

  const handleSaveAppliedCallExplorerFilters = (values) => {
    // Filters first: an applied saved filter carries its own dateRange, and the dialog's choice must win.
    const callExplorerFilter = {
      ...filters,
      name: values.name,
      isDefault: values.isDefault || false,
      dateRange: values.dateRange,
    }

    dispatch(createSavedFilter(callExplorerFilter, CALL_EXPLORER_FILTER_TYPE))
  }

  const handleToggleCategory = (selectedCategory) => {
    dispatch(setFilters(toggleEventCategory(filters, selectedCategory)))
  }

  const handleScorecardsThreshold = (threshold) => {
    handleUpdateFilters('scorecardThreshold', threshold)
  }

  const handleMultiScorecardConfigSelect = (multiScorecardOptions) => {
    handleUpdateFilters('scorecards', multiScorecardOptions)
  }

  const handleToggleIncludes = (section) => {
    dispatch(setFilters(toggleEventIncludes(filters, section)))
  }
  const handleToggleDispositionsEntry = (selectedNode) => {
    const { value } = selectedNode
    const { selected = [], includes } = filters.dispositions

    const isAlreadySelected = selected.some((item) => item.value === value)

    const updatedSelected = isAlreadySelected
      ? selected.filter((item) =>
          typeof item === 'string' ? item !== value : item.value !== value
        )
      : [...selected, { value }]

    dispatch(
      setFilters({
        dispositions: {
          selected: updatedSelected,
          includes,
        },
      })
    )
  }
  const entrySelectionDisabled = filters.playbooks.length === 0

  const dispositionsOptions = data.dispositions.map((dispo) => ({
    ...dispo,
    section: 'dispositions',
  }))
  const checklistOptions = formatNestedOptions(
    'checklist',
    data.playbookData?.map((playbook) => {
      return {
        category: playbook.playbook_name.replaceAll(' - ', '-'),
        items: playbook.checklist || [],
        cid: playbook.cid,
        playbookName: playbook.playbook_name,
      }
    }) || [],
    {
      ...filters.checklist,
      openCategories: filters.openCategories,
      openSubcategories: filters.openSubcategories,
    }
  )

  const notificationOptions = formatNestedOptions(
    'notifications',
    data.playbookData?.map((playbook) => {
      return {
        category: playbook.playbook_name.replaceAll(' - ', '-'),
        items: playbook.notifications || [],
        cid: playbook.cid,
        playbookName: playbook.playbook_name,
      }
    }) || [],
    {
      ...filters.notifications,
      openCategories: filters.openCategories,
    }
  )
  const deckOptions = formatNestedOptions(
    'deck',
    data.playbookData?.flatMap((playbook) => playbook.deck || []),
    {
      ...filters.deck,
      openCategories: filters.openCategories,
      openSubcategories: filters.openSubcategories,
    }
  )
  const deckSublists = Object.values(
    deckOptions.reduce((acc, obj) => {
      if (!acc[obj.playbookName]) {
        acc[obj.playbookName] = []
      }
      acc[obj.playbookName].push(obj)
      return acc
    }, {})
  )

  const updatedPlaybookData = data.playbookData.map((playbook) => {
    const postcalls = playbook.postcall.map((postcall) => {
      return {
        ...postcall,
        playbookName: playbook.playbook_name,
        cid: playbook.cid,
      }
    })
    return {
      ...playbook,
      postcall: postcalls,
    }
  })

  const postcallOptions = formatNestedOptions(
    'postcall',
    updatedPlaybookData?.flatMap((playbook) => playbook.postcall || []),
    {
      ...filters.postcall,
      openCategories: filters.openCategories,
      openSubcategories: filters.openSubcategories,
    }
  )

  const postcallSublists = Object.values(
    postcallOptions.reduce((acc, obj) => {
      if (!acc[obj.playbookName]) {
        acc[obj.playbookName] = []
      }
      acc[obj.playbookName].push(obj)
      return acc
    }, {})
  )

  const filterDisabledReason = filterIsDisabled()
  const alwaysVisibleFilters = ['duration']
  const filterDisplayValues = getFilterDisplayValues(filters, [], false, true)
  const shouldDisplayValue = (accessor) =>
    filterValueHasChanged(initialState, filters, accessor, alwaysVisibleFilters)
  const filterValuesApplied = Object.entries(filterDisplayValues).filter(([accessor]) =>
    shouldDisplayValue(accessor)
  )
  const filterDateRangeLabel = getDateRangeLabel(filters)
  const showSaveFiltersButton = !isEmpty(filterValuesApplied) || filterDateRangeLabel !== 'Today'
  const isMessageChannel =
    hasChannelAccess(orgActiveChannels, filters.channel, isBaltoAdmin) &&
    filters.channel !== CHANNEL_TYPE.CALL
  const supportsCustomerIdFilter = CHANNELS_WITH_CUSTOMER_ID.includes(filters.channel)
  const messageCountInvalid =
    Boolean(filters.minMessageCount) &&
    Boolean(filters.maxMessageCount) &&
    Number(filters.minMessageCount) > Number(filters.maxMessageCount)
  const maxPossibleMessageCount = 255
  const maxPossibleCallLengthMinutes = 10000
  const callFilters = (
    <>
      {!isHidden(AI_AGENT_FILTERS) && (
        <FormField data-testid="agent-type">
          <label>Agent Type</label>
          <Select
            isClearable
            placeholder="All"
            options={agentTypeOptions}
            value={filters.agentType}
            onChange={(option) => {
              handleUpdateFilters('agentType', option ? option.value : '')
            }}
          />
        </FormField>
      )}
      <FormField data-testid="call-duration">
        <label>Min. Call Duration</label>
        <Select
          isClearable={false}
          allowFreeText
          isNumberField
          placeholder="Minutes per call"
          options={minCallDurationOptions}
          value={filters.callDuration}
          onChange={(option, action) => {
            action.action === 'clear'
              ? handleUpdateFilters('callDuration', '1')
              : handleUpdateFilters('callDuration', option.value)
          }}
        />
      </FormField>

      <FormField data-testid="max-call-duration">
        <label>Max Call Duration</label>
        <TextInput
          type="number"
          placeholder="Minutes per call"
          value={filters.maxCallDuration}
          onChange={(event) => {
            if (event.target.value < maxPossibleCallLengthMinutes) {
              handleUpdateFilters('maxCallDuration', event.target.value || '')
            }
          }}
          error={callDurationInvalid}
        />
        {callDurationInvalid && (
          <ErrorMessage content="Max must be greater than min" data-testid="callduration-error" />
        )}
      </FormField>
      {!isAudioIngestionOnly && (
        <WinsFilter
          filters={filters}
          options={[
            { value: 'true', label: 'Only wins' },
            { value: 'false', label: 'Only non-wins' },
          ]}
          onChange={(option, action) => {
            handleUpdateFilters('isWin', action.action === 'clear' ? '' : option.value)
          }}
          fixedWidth={false}
        />
      )}
      <FormField data-testid="playbook-filter">
        <label>Playbooks</label>
        <MultiSelect
          isSelectAll
          placeholderPill="All Playbooks"
          label="Playbook"
          options={data.playbooks}
          optionsLength={data.playbooks ? data.playbooks.length : 0}
          loading={loading.playbooks}
          value={filters.playbooks}
          onChange={(value) => {
            return handleUpdateFilters('playbooks', value)
          }}
        />
      </FormField>
      <FormField data-testid="voip-campaign-filter">
        <label className="flex-align-center small-gap">
          VOIP Campaigns
          {voipCampaignsTimedOut && (
            <InteractiveTooltip
              content="There are too many calls within this date range to filter the available VOIP Campaigns, so all campaigns are shown."
              placement="top"
            >
              <IconInfoCircle size={14} className="status-informative" />
            </InteractiveTooltip>
          )}
        </label>
        <MultiSelect
          isSelectAll
          placeholder="Select VOIP Campaigns"
          label="VOIP Campaign Name"
          options={voipCampaignsOptions}
          optionsLength={voipCampaignsOptions.length}
          loading={loading.voipCampaigns}
          value={filters.voipCampaigns}
          onChange={(value) => handleUpdateFilters('voipCampaigns', value)}
        />
      </FormField>
      {!isAudioIngestionOnly && (
        <FormField data-testid="checklist-filter">
          <label>Checklist</label>
          <NestedSelect
            disabled={entrySelectionDisabled}
            options={checklistOptions}
            value={filters.checklist.selected}
            includes={filters.checklist.includes}
            section="checklist"
            clearAll={clearAllEntries}
            toggleIncludes={handleToggleIncludes}
            toggleEntry={handleToggleEntry}
            toggleCategory={handleToggleCategory}
          />
        </FormField>
      )}
      {!isAudioIngestionOnly && (
        <FormField data-testid="dynamic-prompt-filter">
          <label>Dynamic Prompts</label>
          <NestedSelect
            disabled={entrySelectionDisabled}
            options={deckOptions}
            anotherOptions={deckSublists}
            value={filters.deck.selected}
            includes={filters.deck.includes}
            openCategories={filters.openCategories}
            openSubcategories={filters.openSubcategories}
            section="deck"
            clearAll={clearAllEntries}
            toggleIncludes={handleToggleIncludes}
            toggleEntry={handleToggleEntry}
            toggleCategory={handleToggleCategory}
          />
        </FormField>
      )}
      {!isAudioIngestionOnly && (
        <FormField data-testid="notification-filter">
          <label>Notifications</label>
          <NestedSelect
            disabled={entrySelectionDisabled}
            options={notificationOptions}
            value={filters.notifications.selected}
            includes={filters.notifications.includes}
            section="notifications"
            clearAll={clearAllEntries}
            toggleIncludes={handleToggleIncludes}
            toggleEntry={handleToggleEntry}
            toggleCategory={handleToggleCategory}
          />
        </FormField>
      )}
      {!isAudioIngestionOnly && (
        <FormField data-testid="postcall-filter">
          <label>Post Call</label>
          <NestedSelect
            disabled={entrySelectionDisabled}
            options={postcallOptions}
            anotherOptions={postcallSublists}
            value={filters.postcall.selected}
            includes={filters.postcall.includes}
            openCategories={filters.openCategories}
            openSubcategories={filters.openSubcategories}
            section="postcall"
            clearAll={clearAllEntries}
            toggleIncludes={handleToggleIncludes}
            toggleEntry={handleToggleEntry}
            toggleCategory={handleToggleCategory}
          />
        </FormField>
      )}
      {isCallExplorerPage && (
        <FormField data-testid="attachment-filter">
          <label>Attachments</label>
          <MultiSelect
            placeholder="With or Without Attachments"
            options={attachmentOptions}
            value={filters.attachments}
            onChange={(value) => {
              handleUpdateFilters('attachments', value)
            }}
          />
        </FormField>
      )}
      {isCallExplorerPage && !isAudioIngestionOnly && (
        <FormField data-testid="sentiment-filter">
          <label>Customer Ending Sentiment</label>
          <MultiSelect
            placeholder="All sentiment"
            options={sentimentOptions}
            value={filters.endingSentiment}
            onChange={(value) => {
              handleUpdateFilters('endingSentiment', value)
            }}
          />
        </FormField>
      )}
      {isCallExplorerPage && (
        <FormField data-testid="direction-filter">
          <label>Call Direction</label>
          <Select
            isClearable
            placeholder="Inbound and Outbound"
            options={directionOptions}
            value={filters.direction}
            onChange={(option) => {
              handleUpdateFilters('direction', option?.value || '')
            }}
          />
        </FormField>
      )}
      {outboundCampaignsEnabled && (
        <FormField data-testid="outbound-campaign-name-filter">
          <label>Outbound Campaign Name</label>
          <MultiSelect
            isSelectAll
            placeholder="All Outbound Campaigns"
            label="Outbound Campaign Name"
            options={data.outboundCampaigns}
            optionsLength={data.outboundCampaigns.length}
            loading={loading.outboundCampaigns}
            value={filters.outboundCampaigns}
            onChange={(value) => handleUpdateFilters('outboundCampaigns', value)}
          />
        </FormField>
      )}
      {togoEnabled && !isHidden(AI_AGENT_FILTERS) && (
        <FormField data-testid="outcome-filter">
          <label>AI Agent Outcome</label>
          <MultiSelect
            isSelectAll
            placeholder="All Outcomes"
            label="AI Agent Outcome"
            options={data.aiAgentOutcomes}
            optionsLength={data.aiAgentOutcomes.length}
            loading={loading.aiAgentOutcomes}
            value={filters.aiAgentOutcomes}
            onChange={(value) => handleUpdateFilters('aiAgentOutcomes', value)}
          />
        </FormField>
      )}
      {togoEnabled && !isHidden(AI_AGENT_FILTERS) && (
        <FormField data-testid="disconnection-reason-filter">
          <label>AI Agent Disconnection Reason</label>
          <MultiSelect
            isSelectAll
            placeholder="All Reasons"
            label="AI Agent Disconnection Reason"
            options={data.aiAgentDisconnectionReasons}
            optionsLength={data.aiAgentDisconnectionReasons.length}
            loading={loading.aiAgentDisconnectionReasons}
            value={filters.aiAgentDisconnectionReasons}
            onChange={(value) => handleUpdateFilters('aiAgentDisconnectionReasons', value)}
          />
        </FormField>
      )}
    </>
  )

  const messageFilters = (
    <FormField data-testid="message-count-filter">
      <label>Message Count</label>
      <div className="full-width flex-space-between gap ">
        <div style={{ width: '45%' }}>
          <TextInput
            type="number"
            placeholder="Min message count"
            value={filters.minMessageCount}
            onChange={(event) => {
              if (event.target.value < maxPossibleMessageCount) {
                handleUpdateFilters('minMessageCount', event.target.value || '')
              }
            }}
            error={messageCountInvalid}
          />
          {messageCountInvalid && (
            <ErrorMessage content="Min must be less than max" data-testid="minMessageCount-error" />
          )}
        </div>
        <div style={{ lineHeight: '2.5rem' }}>-</div>
        <div style={{ width: '45%' }}>
          <TextInput
            type="number"
            placeholder="Max message count"
            value={filters.maxMessageCount}
            onChange={(event) => {
              if (event.target.value < maxPossibleMessageCount) {
                handleUpdateFilters('maxMessageCount', event.target.value || '')
              }
            }}
            error={messageCountInvalid}
          />
          {messageCountInvalid && (
            <ErrorMessage
              content="Max must be greater than min"
              data-testid="maxMessageCount-error"
            />
          )}
        </div>
      </div>
    </FormField>
  )

  const groupFilters = (
    <>
      {hasOrganizationDropdown && (
        <OrganizationFilter
          dataTestId={
            showManagerHierarchyOrgDropdown
              ? 'hierarchy-organization-dropdown'
              : 'organization-dropdown'
          }
          options={
            showManagerHierarchyOrgDropdown ? hierarchyOrganizationOptions : organizationOptions
          }
          value={organizationId}
          onChange={handleOrgSelect}
          fixedWidth={false}
          isClearable={false}
        />
      )}
      <FormField data-testid="date-picker">
        <label>Date Range</label>
        <DateRangePickerReusable
          onChange={handleUpdateRange}
          startDate={startDate}
          endDate={endDate}
          pickedPreset={filters.dateRange ?? null}
          error={dateInvalid}
          allow120Days={allow120Days}
          saveOrCloseOnClickPreset
        />
        {dateInvalid && (
          <ErrorMessage
            content={`Date range must be ${allow120Days ? 120 : 60} days or less`}
            data-testid="datepicker-error"
          />
        )}
      </FormField>
      <FormField data-testid="agents-filter">
        <label>Agents</label>
        <MultiSelect
          isSelectAll
          placeholderPill="All Agents"
          label="Agent"
          options={!agentSearchQuery && data.agents.length > 1000 ? [] : data.agents}
          optionsLength={data.agents.length}
          loading={loading.agents}
          value={filters.agents}
          formatOptionLabel={(option) => {
            return (
              <div className="flex">
                {option.label}
                {option.isAi && <AiSparkles />}
              </div>
            )
          }}
          onInputChange={(inputValue) => {
            setAgentSearchQuery(inputValue)
          }}
          onChange={(value) => {
            handleUpdateFilters('agents', value)
          }}
          noOptionsMessage={() =>
            agentSearchQuery ? 'No results found' : 'Begin typing to search...'
          }
        />
      </FormField>
      <FormField data-testid="tags-filter">
        <label>Tags</label>
        <MultiSelect
          placeholderPill="All Tags"
          placeholder="Select Tags"
          options={data.tags}
          loading={loading.tags}
          value={filters.tags}
          onChange={(value) => handleUpdateFilters('tags', value)}
        />
      </FormField>
      {showScorecards && (
        <>
          <FormField data-testid="scorecard-config-field">
            <label id="scorecards">Scorecards</label>
            <MultiSelect
              isSelectAll
              placeholderPill="All Scorecards"
              options={scorecardOptions}
              aria-labelledby="scorecards"
              loading={loading.scorecards}
              value={filters?.scorecards}
              onChange={(value) => handleMultiScorecardConfigSelect(value)}
            />
          </FormField>
          <PartialScoresFilter
            isDisabled={isEmpty(filters?.scorecards)}
            filters={filters}
            placeholder="Select Status"
            fixedWidth={false}
            onChange={(option) => {
              handleUpdateFilters('scoredStatus', option != null ? option.value : '')
            }}
          />
          <FormField data-testid="scorecard-threshold-filter">
            <label>Threshold</label>
            <InteractiveTooltip
              content="Please select at least one scorecard"
              hide={!isEmpty(filters.scorecards)}
              block
            >
              <Select
                placeholder="Select Threshold"
                options={[
                  { label: 'Needs improvement', value: 'improvement_threshold' },
                  { label: 'Meets expectations', value: 'meets_threshold' },
                  { label: 'Exceeds expectations', value: 'exceeds_threshold' },
                ]}
                aria-labelledby="scorecard_threshold"
                value={filters?.scorecardThreshold}
                isDisabled={isEmpty(filters.scorecards)}
                onChange={(selectedOption) => {
                  if (selectedOption === null) {
                    handleScorecardsThreshold('')
                  } else {
                    handleScorecardsThreshold(selectedOption.value)
                  }
                }}
              />
            </InteractiveTooltip>
          </FormField>
        </>
      )}
      {!isEmpty(data.dispositions) && (
        <FormField data-testid="dispositions-dropdown">
          <label>Dispositions</label>
          <NestedSelect
            options={dispositionsOptions}
            value={filters.dispositions.selected}
            includes={filters.dispositions.includes}
            disabled={loading.dispositions}
            section="dispositions"
            toggleIncludes={handleToggleIncludes}
            toggleEntry={handleToggleDispositionsEntry}
            clearAll={clearAllEntries}
          />
        </FormField>
      )}
    </>
  )

  const keywordFilter = !isHidden(KEYWORD_FILTERS) && (
    <KeywordSearch handleUpdateFilters={handleUpdateFilters} filters={filters} />
  )
  const voipCustomerFilter = (
    <VoipCustomerSearch handleUpdateFilters={handleUpdateFilters} filters={filters} />
  )

  // When playbooks are filtered, a bit-field option only applies if it belongs
  // to a selected playbook; these helpers are shared with the applied-filter
  // pills so both surfaces grey / gate options the same way.
  const selectedPlaybookCids = getSelectedPlaybookCids(filters)

  // The Notetaker (bit-field) filters are grouped into one collapsible section
  // pinned to the bottom of the modal, so they don't crowd the core filters.
  // Was gated to Call Explorer and Discover by route. Every page that embeds
  // this bar wants it, and the board filters on bit flow fields server-side, so
  // it is opt-out like the rest rather than an allowlist of paths.
  const notetakerFilter = !isHidden(NOTETAKER_FILTERS) && (
    <Accordion
      styled
      className="notetaker-filter-group"
      id="notetaker-filters"
      title={
        <span className="notetaker-filter-title">
          Notetaker
          <InteractiveTooltip content="Notetaker filters cannot be saved currently">
            <IconInfoCircle
              size={16}
              className="notetaker-filter-title__info"
              data-testid="notetaker-filter-info"
            />
          </InteractiveTooltip>
        </span>
      }
      expanded={notetakerExpanded}
      handleToggle={() => setNotetakerExpanded((expanded) => !expanded)}
    >
      {isEmpty(data.bitFields) ? (
        // Don't flash the empty message while the fields are still loading.
        !loading.bitFields && (
          <p className="notetaker-filter-group__empty" data-testid="notetaker-no-fields">
            No Notetaker fields configured that are filterable. Go create a Pick List or Yes/No type
            field to enable filtering by its results.
          </p>
        )
      ) : (
        <div className="notetaker-filter-group__fields">
          {data.bitFields.map((field) => {
            // Boolean fields filter by a single true/false value, not taxonomy
            // options, so render a Yes/No selector.
            if (isBooleanBitField(field)) {
              return (
                <FormField key={field.field_id} data-testid={`bit-field-filter-${field.field_id}`}>
                  <label>{field.field_name}</label>
                  <Select
                    isClearable
                    placeholder="Yes/No"
                    menuPortalTarget={MENU_PORTAL_TARGET}
                    menuPlacement="auto"
                    options={BOOLEAN_FIELD_OPTIONS}
                    value={filters.booleanFields?.[field.field_id]}
                    onChange={(option) =>
                      handleUpdateBooleanField(field.field_id, option ? option.value : null)
                    }
                  />
                </FormField>
              )
            }

            const fieldOptions = buildBitFieldOptions(field, selectedPlaybookCids)
            // No option belongs to a filtered playbook — the whole field is moot.
            const noOptionsApply = fieldOptions.every((option) => !option.applicable)

            const select = (
              <MultiSelect
                placeholder={`All ${field.field_name}`}
                menuPortalTarget={MENU_PORTAL_TARGET}
                // The group is pinned to the modal bottom, so flip the menu up
                // when there isn't room below rather than letting it clip.
                menuPlacement="auto"
                disabled={noOptionsApply}
                isOptionDisabled={isBitFieldOptionDisabled}
                formatOptionLabel={renderBitFieldOption}
                options={fieldOptions}
                value={filters.bitFields?.[field.field_id] || []}
                onChange={(value) => handleUpdateBitField(field.field_id, value)}
              />
            )

            return (
              <FormField key={field.field_id} data-testid={`bit-field-filter-${field.field_id}`}>
                <label>{field.field_name}</label>
                {noOptionsApply ? (
                  <InteractiveTooltip block content="Playbook selected does not contain this field">
                    {select}
                  </InteractiveTooltip>
                ) : (
                  select
                )}
              </FormField>
            )
          })}
        </div>
      )}
    </Accordion>
  )

  return (
    <>
      <div className="flex-align-start flex-space-between small-gap mb full-width">
        <div className="flex-align-start small-gap flex-wrap">
          <Button small brand onClick={handleOpenModal} data-testid="call-search-filters-button">
            <IconPlus />
            Filters
          </Button>
          <CallSearchPills
            hiddenFilters={hiddenFilters}
            onApplyFilters={onApplyFilters}
            filterValuesApplied={filterValuesApplied}
            handleSubmitFilters={handleSubmitFilters}
            handleClearRange={handleClearRange}
            handleUpdateRange={(selection) => handleUpdateRange(selection, true)}
            dateInvalid={dateInvalid}
            modalOpen={modalOpen}
            handleOpenFilterModal={handleOpenModal}
            allow120Days={allow120Days}
            channelType={filters.channel}
          />
          {!childOrgSelected && showSaveFiltersButton && (
            <InteractiveTooltip content={filterDisabledReason} hide={!filterDisabledReason}>
              <SaveCallFilter
                filterDateRangeLabel={filterDateRangeLabel}
                handleSave={handleSaveAppliedCallExplorerFilters}
                filterValuesApplied={filterValuesApplied}
              />
            </InteractiveTooltip>
          )}
        </div>

        <div className="flex-align-center small-gap">
          <Button
            small
            onClick={handleClearFilters}
            className="no-wrap"
            data-testid="call-search-reset-button"
          >
            Clear Filters
          </Button>
        </div>
      </div>

      {modalOpen && (
        <BasicModal
          data-testid="call-search-filters-modal"
          title="Filters"
          onClose={handleDismissModal}
          show={modalOpen}
          size="large"
        >
          <form className="call-search-filters filter-form" onSubmit={handleSubmitFilters}>
            <div className="call-search-grid">
              {groupFilters}
              {isMessageChannel ? messageFilters : callFilters}
              {keywordFilter}
              {supportsCustomerIdFilter && voipCustomerFilter}
              {!isMessageChannel && notetakerFilter}
            </div>
            <div className="modal-footer flex-space-between">
              <Button
                onClick={() => {
                  handleClearFilters()
                  handleCloseModal()
                }}
                data-testid="modal-clear-filters-button"
              >
                Clear Filters
              </Button>
              <div className="flex-align-center small-gap">
                <Button
                  data-testid="dismiss-filters-button"
                  content="Cancel"
                  onClick={handleDismissModal}
                />
                <Button
                  type="submit"
                  brand
                  disabled={!!filterDisabledReason}
                  disabledTooltip={filterDisabledReason}
                  content="Apply"
                  data-testid="submit-filters-button"
                />
              </div>
            </div>
          </form>
        </BasicModal>
      )}
    </>
  )
}

export const CallSearchFilters = CallSearchFiltersComponent
